import React, { Fragment, useState, useEffect } from 'react';
import { Container, Accordion, AccordionDetails, AccordionSummary, Grid } from '@mui/material';
import './PremiumFinancingAnalysis.scss';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import AccessCodeGate from '../../../../components/AccessCodeGate/AccessCodeGate';
import { selectedLocale } from '../../../../store/locale/action';
import { renderHeaderButton } from '../../../../store/headerButtonControl/duck';
import AppContentContainer, {
  APP_CONTENT_CONTAINER_MAX_WIDTH
} from '../../../../components/AppContentContainer/AppContentContainer';
import PremiumFinancing from './PremiumFinancing';
import { getPDF } from '../../../../store/pdf/api';
import NativeOrWeb from '../../../../utils/native';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import PDFExportCard from './PDFExportCard/PDFExportCard';
import PreviewWrapper from '../../../../pdf-templates/PreviewWrapper';
import FinancialChartsButton from '../../../Analysis/partials/FinancialChartsButton/FinancialChartsButton';
import { FINANCIAL_CHARTS_HIBOR_ITEM } from '../../../../constants/financial-charts';
import { toggleDialog } from '../../../../store/control/duck';
import {
  getPFinCalculatorProfiles,
  savePFinCalculatorProfiles,
  updatePFinCalculatorInputs,
  updatePFinCalculatorProfiles
} from '../../../../store/pFinCalculator/duck';
import LoadingBackdrop from '../../../../components/LoadingBackdrop/LoadingBackdrop';
import { LOADING, ERROR, SUCCESS } from '../../../../constants/phase';
import IconToast from '../../../../components/NewToast';
import ConfirmSaveDialog from '../../../Analysis/FinancialPyramid/ConfirmSaveDialog/ConfirmSaveDialog';
import LeavePageConfirm from '../../../../components/LeavePageConfirm/LeavePageConfirm';
import ExportHintDialog from '../../../../components/ExportHintDialog/ExportHintDialog';
import SideLoanInterestInput from './SideLoanInterestInput/SideLoanInterestInput';
import LoanCalculatorDialog from './LoanCalculatorDialog/LoanCalculatorDialog';
import LoanInterestCalculatorDialog from './LoanInterestCalculatorDialog/LoanInterestCalculatorDialog';
import {
  getDefaultPremiumFinancingProfile,
  getPremiumFinancingProfileDisplay
} from '../../../../utils/premium-financing';
import produce from 'immer';
import { usePrevious } from '../../../../utils/hooks';
import { tracking } from '../../../../utils/tracking';
import BonusCalculatorDialog from './BonusCalculatorDialog/BonusCalculatorDialog';
import { countDecimals, roundNumber } from '../../../../utils/formatNumber';
import EditDisplayNamesDialog from '../../../../components/EditDisplayNamesDialog/EditDisplayNamesDialog';
import AladdinHint from '../../../../components/AladdinHint/AladdinHint';
import { trackDirect } from '../../../../store/analytics/api';
import { trackViewership } from '../../../../utils/tracking/index';
import { refreshCashValuesFromLinkedIllustration } from '../../../../utils/linked-illustration';
import { getEffectiveReturnRealizationRate } from './return-realization-rate';
import { getAverageValue } from '../../../../utils/number';
import { getIllustrations } from '../../../../store/illustration/duck';
import { syncPremiumFinancingIllustrationLinks } from '../../IllustrationOcr/illustrationConsumer';

const roundNumberToOneDec = num => Math.round(num * 10) / 10;
const getOverrideLoanInterestSliderValue = (profiles, index, defaultProfile) =>
  _.get(profiles, `[${index}].bankLoanInterest`, defaultProfile.bankLoanInterest);
const getDynamicLoanInterestExpanded = (profiles, index, defaultProfile) =>
  Array.from(
    Array(
      _.get(profiles, `[${index}].yearsToShow`, defaultProfile.yearsToShow)[
        _.get(profiles, `[${index}].yearsToShow`, defaultProfile.yearsToShow).length - 1
      ]
    ).keys()
  ).map(() => false);
const getNewTermLoanPercentage = ({ termLoanPercentage, yearsToShow, bankLoanInterest }) => {
  if (termLoanPercentage && Array.isArray(termLoanPercentage)) {
    const defaultBankLoanInterest = termLoanPercentage[termLoanPercentage.length - 1];
    return Array.from(Array(yearsToShow[yearsToShow.length - 1]).keys()).map((v, index) => {
      return index < termLoanPercentage.length
        ? termLoanPercentage[index] ?? defaultBankLoanInterest
        : defaultBankLoanInterest;
    });
  } else {
    return Array.from(Array(yearsToShow[yearsToShow.length - 1]).keys()).map(() => bankLoanInterest);
  }
};

export const buildUniformTermLoanPercentage = ({ termLoanPercentage, yearsToShow, rate }) => {
  const existingLength = Array.isArray(termLoanPercentage) ? termLoanPercentage.length : 0;
  const validYears = Array.isArray(yearsToShow)
    ? yearsToShow.map(Number).filter(year => Number.isFinite(year) && year > 0)
    : [];
  const maxYear = validYears.length ? Math.max(...validYears) : 0;
  const length = existingLength || Math.floor(maxYear);

  return Array.from({ length }, () => rate);
};

export const applyBulkLoanInterest = (profile, rate) => ({
  ...profile,
  bankLoanInterest: rate,
  overrideLoanInterestSliderValue: rate,
  termLoanPercentage: buildUniformTermLoanPercentage({
    termLoanPercentage: profile?.termLoanPercentage,
    yearsToShow: profile?.yearsToShow,
    rate
  })
});

export const getAverageTermLoanInterest = termLoanPercentage => {
  if (
    !Array.isArray(termLoanPercentage) ||
    termLoanPercentage.length === 0 ||
    termLoanPercentage.some(rate => !Number.isFinite(Number(rate)))
  ) {
    return null;
  }

  return roundNumber(getAverageValue(termLoanPercentage), 3);
};

const getNewTermAddOn = ({ termAddOn, yearsToShow }) => {
  if (termAddOn) {
    return Array.from(Array(yearsToShow[yearsToShow.length - 1]).keys()).map((v, index) => {
      return index < termAddOn.length ? termAddOn[index] ?? 0 : 0;
    });
  } else {
    return Array.from(Array(yearsToShow[yearsToShow.length - 1]).keys()).map(() => 0);
  }
};

const PremiumFinancingAnalysis = props => {
  const {
    intl,
    safariGenerate,
    trial,
    userDetails,
    loading,
    pFinProfiles,
    getProfilesLoadState,
    updateProfilesLoadState,
    updatePFinCalculatorProfiles,
    savePFinCalculatorProfiles,
    updatePFinCalculatorInputs,
    selectedLocale,
    renderHeaderButton,
    getPFinCalculatorProfiles,
    getIllustrations
  } = props;

  // constructor(props) {
  //   super(props);
  //   let state = {
  //     language: _.get(props.userDetails, 'language', 'en'),
  //     profileIndex: 0,
  //     confirmSaveDialog: false,
  //     updated: false,
  //     loaded: false,
  //     graphMode: 'pfa-return-combo-title',
  //     hintDialog: false,
  //     rowOpen: [false, false, false, false],
  //     fixedLoanInterestExpanded: false,
  //     loanCalculatorDialog: false
  //   };
  //   state = { ...state, ...props.pFinProfiles[0] };
  //   state.overrideLoanInterestSliderValue = state.bankLoanInterest;
  //   state.dynamicLoanInterestExpanded = Array.from(Array(state.yearsToShow[state.yearsToShow.length - 1]).keys()).map(
  //       () => false
  //   );
  //
  //   this.state = _.merge(state, props.location.state);
  // }
  const defaultProfile = getDefaultPremiumFinancingProfile();
  const defaultProfileIndex = 0;

  const [internalProfiles, setInternalProfiles] = useState(_.cloneDeep(props.pFinProfiles));
  const [language, setLanguage] = useState(_.get(userDetails, 'language', 'zh'));
  const [profileIndex, setProfileIndex] = useState(defaultProfileIndex);
  const [confirmSaveDialog, setConfirmSaveDialog] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [graphMode, setGraphMode] = useState('pfa-return-combo-title');
  const [hintDialog, setHintDialog] = useState(false);
  const [rowOpen, setRowOpen] = useState([false, false, false, false]);
  const [fixedLoanInterestExpanded, setFixedLoanInterestExpanded] = useState(false);
  const [loanCalculatorDialog, setLoanCalculatorDialog] = useState(false);
  const [bonusCalculatorDialog, setBonusCalculatorDialog] = useState(false);
  const [loanInterestCalculatorDialog, setLoanInterestCalculatorDialog] = useState(false);
  const [overrideLoanInterestSliderValue, setOverrideLoanInterestSliderValue] = useState(
    getOverrideLoanInterestSliderValue(pFinProfiles, defaultProfileIndex, defaultProfile)
  );
  const [dynamicLoanInterestExpanded, setDynamicLoanInterestExpanded] = useState(
    getDynamicLoanInterestExpanded(pFinProfiles, defaultProfileIndex, defaultProfile)
  );
  const [disableLoan, setDisableLoan] = useState(false);
  const [isEditDisplayNamesDialogOpen, setIsEditDisplayNamesDialogOpen] = useState(false);

  const prevGetProfilesLoadState = usePrevious(getProfilesLoadState);
  const prevUpdateProfilesLoadState = usePrevious(updateProfilesLoadState);

  const isGuest = userDetails._id === undefined;
  const currentProfile = _.get(internalProfiles, `[${profileIndex}]`, defaultProfile);
  const {
    principal,
    base,
    loanPercentage,
    bankLoanInterest,
    policyInterest,
    yearsToShow,
    cashValueAt,
    firstYearBonus,
    termLoanPercentage,
    termAddOn,
    overrideLoanInterest,
    day0SurrenderValue,
    bankLoanPercentage,
    shouldPayPrincipal,
    isCurrencyEnabled,
    currency,
    hiborBase,
    hiborAddOn,
    primeBase,
    primeSubtract
  } = currentProfile || {};

  const handleChange = event => {
    const newInternalProfiles = produce(internalProfiles, draft => {
      const { name, value } = event.target;

      // avoid overwriting a more precise loanPercentage value
      if (name === 'loanPercentage') {
        if (countDecimals(loanPercentage) > 1 && roundNumber(loanPercentage, 1) === value) {
          return;
        }
      }

      if (name !== 'disableLoan' && name !== 'importIllustration') {
        draft[profileIndex][name] = value;
      }

      if (name === 'importIllustration') {
        const {
          principal: nextPrincipal,
          cashValueAt: nextCashValueAt,
          loanPercentage: nextLoanPercentage,
          firstYearBonus: nextFirstYearBonus,
          base: nextBase,
          linkedIllustration: nextLinkedIllustration,
          returnRealizationRateEnabled: nextRateEnabled,
          returnRealizationRate: nextRate,
          day0SurrenderValue: nextDay0SurrenderValue
        } = value || {};

        if (nextPrincipal != null) {
          draft[profileIndex].principal = nextPrincipal;
        }
        if (nextLoanPercentage != null) {
          draft[profileIndex].loanPercentage = nextLoanPercentage;
          setDisableLoan(false);
        }
        if (nextFirstYearBonus != null) {
          draft[profileIndex].firstYearBonus = nextFirstYearBonus;
        }
        if (nextBase != null) {
          draft[profileIndex].base = nextBase;
        } else if (nextPrincipal != null && nextLoanPercentage == null && nextFirstYearBonus == null) {
          // legacy: only principal provided — recompute base from current loan ratio
          const finalLoanPercentage = disableLoan ? 0 : loanPercentage;
          if (!nextPrincipal) {
            draft[profileIndex].loanPercentage = 0;
            draft[profileIndex].base = 0;
            draft[profileIndex].firstYearBonus = 0;
          } else if (finalLoanPercentage || finalLoanPercentage === 0) {
            draft[profileIndex].base = nextPrincipal * (1 - finalLoanPercentage / 100);
            draft[profileIndex].firstYearBonus = 0;
          } else {
            draft[profileIndex].base = nextPrincipal;
            draft[profileIndex].firstYearBonus = 0;
          }
        }
        if (nextCashValueAt) {
          draft[profileIndex].cashValueAt = nextCashValueAt;
        }
        if (nextLinkedIllustration !== undefined) {
          draft[profileIndex].linkedIllustration = nextLinkedIllustration;
        }
        if (nextRateEnabled != null) {
          draft[profileIndex].returnRealizationRateEnabled = nextRateEnabled;
        }
        if (nextRate != null) {
          draft[profileIndex].returnRealizationRate = nextRate;
        }
        if (nextDay0SurrenderValue != null) {
          draft[profileIndex].day0SurrenderValue = nextDay0SurrenderValue;
        }
      } else if (name === 'returnRealizationRate' || name === 'returnRealizationRateEnabled') {
        // Always refresh cash values from linked illustration when rate toggles / changes
        const linked = draft[profileIndex].linkedIllustration;
        if (linked) {
          const rate = getEffectiveReturnRealizationRate(
            draft[profileIndex].returnRealizationRateEnabled,
            draft[profileIndex].returnRealizationRate
          );
          const refreshed = refreshCashValuesFromLinkedIllustration(
            draft[profileIndex].yearsToShow,
            draft[profileIndex].cashValueAt,
            linked,
            rate
          );
          if (refreshed) {
            draft[profileIndex].cashValueAt = refreshed;
          }
        }
      } else if (!isCurrencyEnabled || name === 'overrideLoanInterestSliderValue') {
        const finalLoanPercentage = disableLoan ? 0 : loanPercentage;

        if (name === 'yearsToShow') {
          const linked = draft[profileIndex].linkedIllustration;
          const rate = getEffectiveReturnRealizationRate(
            draft[profileIndex].returnRealizationRateEnabled,
            draft[profileIndex].returnRealizationRate
          );
          if (linked) {
            const refreshed = refreshCashValuesFromLinkedIllustration(
              value,
              draft[profileIndex].cashValueAt,
              linked,
              rate
            );
            if (refreshed) {
              draft[profileIndex].cashValueAt = refreshed;
            }
          }

          const newMax = Math.max(...value);
          if (newMax !== Math.max(...(yearsToShow ?? []))) {
            if (overrideLoanInterest) {
              draft[profileIndex].termLoanPercentage = getNewTermLoanPercentage({
                termLoanPercentage,
                yearsToShow: value,
                bankLoanInterest
              });
              draft[profileIndex].termAddOn = getNewTermAddOn({ termAddOn, yearsToShow: value });
            }
          }
        } else if (name === 'principal') {
          const principal = value || 0;
          if (!principal) {
            draft[profileIndex].loanPercentage = 0;
            draft[profileIndex].base = 0;
            draft[profileIndex].firstYearBonus = 0;
          } else {
            if (finalLoanPercentage || finalLoanPercentage === 0) {
              draft[profileIndex].base = principal * (1 - finalLoanPercentage / 100);
              draft[profileIndex].firstYearBonus = 0;
            } else {
              draft[profileIndex].base = principal;
              draft[profileIndex].firstYearBonus = 0;
            }
          }
        } else if (name === 'base') {
          const newBase = value || 0;
          const percentage =
            (principal &&
              (newBase || firstYearBonus || newBase === 0 || firstYearBonus === 0) &&
              (principal - (firstYearBonus ?? 0) - (newBase ?? 0)) / principal) ??
            0;

          if ((percentage || percentage === 0) && base !== newBase) {
            draft[profileIndex].loanPercentage = percentage * 100;
            setDisableLoan(false);
          }
        } else if (name === 'firstYearBonus') {
          const newFirstYearBonus = value || 0;
          if ((base || base === 0) && principal && firstYearBonus !== newFirstYearBonus) {
            draft[profileIndex].base = principal * (1 - (finalLoanPercentage ?? 0) / 100) - (newFirstYearBonus ?? 0);
          }
        } else if (name === 'loanPercentage') {
          const newLoanPercentage = value || 0;
          if (newLoanPercentage !== loanPercentage) {
            // Keep 首年優惠 unless premium − loan − bonus would go negative
            const grossBase = (principal ?? 0) * (1 - newLoanPercentage / 100);
            const baseWithBonus = grossBase - (firstYearBonus ?? 0);
            if (baseWithBonus >= 0) {
              draft[profileIndex].base = Math.round(baseWithBonus);
            } else {
              draft[profileIndex].base = Math.round(grossBase);
              draft[profileIndex].firstYearBonus = 0;
            }
            setDisableLoan(false);
          }
        } else if (name === 'termLoanPercentage') {
          const averageLoanInterest = getAverageTermLoanInterest(value);
          if (averageLoanInterest !== null) {
            draft[profileIndex].bankLoanInterest = averageLoanInterest;
            draft[profileIndex].overrideLoanInterestSliderValue = averageLoanInterest;
            setOverrideLoanInterestSliderValue(averageLoanInterest);
          }
        } else if (name === 'bankLoanInterest' || name === 'overrideLoanInterestSliderValue') {
          const updatedLoanInterest = applyBulkLoanInterest(draft[profileIndex], value);
          draft[profileIndex].bankLoanInterest = updatedLoanInterest.bankLoanInterest;
          draft[profileIndex].overrideLoanInterestSliderValue = updatedLoanInterest.overrideLoanInterestSliderValue;
          draft[profileIndex].termLoanPercentage = updatedLoanInterest.termLoanPercentage;
          setOverrideLoanInterestSliderValue(value);
        } else if (name === 'disableLoan') {
          if (value === true) {
            const base = (principal ?? 0) - (firstYearBonus ?? 0);
            if (base || base === 0) {
              draft[profileIndex].base = Math.round(base);
            }
          } else {
            const base = (principal ?? 0) * (1 - loanPercentage / 100) - (firstYearBonus ?? 0);
            if (base >= 0) {
              draft[profileIndex].base = Math.round(base);
            } else {
              draft[profileIndex].base = (principal ?? 0) * (1 - loanPercentage / 100);
              draft[profileIndex].firstYearBonus = 0;
            }
          }
        } else if (name === 'currency') {
          draft[profileIndex].currency = value;
        }
      }
    });
    setUpdated(true);
    setInternalProfiles(newInternalProfiles);
    // updatePFinCalculatorProfiles(profileIndex, newInternalProfiles[profileIndex]);
  };

  const handleChangeLanguage = event => setLanguage(event.target.value);

  const handleCheckedChange = event => {
    const { name, checked } = event.target;

    const newInternalProfiles = produce(internalProfiles, draft => {
      if (name === 'overrideLoanInterest') {
        draft[profileIndex].overrideLoanInterest = !checked;

        if (!checked) {
          draft[profileIndex].termLoanPercentage = getNewTermLoanPercentage({
            termLoanPercentage,
            yearsToShow,
            bankLoanInterest
          });
          draft[profileIndex].termAddOn = getNewTermAddOn({ termAddOn, yearsToShow });
        }
      } else if (name === 'isCurrencyEnabled') {
        draft[profileIndex].isCurrencyEnabled = checked;
      } else if (name === 'showIRR') {
        draft[profileIndex].showIRR = checked;
      }
    });

    setInternalProfiles(newInternalProfiles);
    setUpdated(true);

    // updatePFinCalculatorProfiles(profileIndex, newInternalProfiles[profileIndex]);
  };

  const handleChangeProfileNames = displayNames => {
    // save new profiles
    const newSaveProfiles = produce(pFinProfiles, draft => {
      for (const [index, displayName] of (displayNames || []).entries()) {
        if (index <= draft.length - 1) {
          draft[index].displayName = displayName;
        }
      }
    });

    if (savePFinCalculatorProfiles) {
      savePFinCalculatorProfiles({ profiles: newSaveProfiles });
    }

    // update internal profiles
    const newInternalProfiles = produce(internalProfiles, draft => {
      for (const [index, displayName] of (displayNames || []).entries()) {
        if (index <= draft.length - 1) {
          draft[index].displayName = displayName;
        }
      }
    });

    setInternalProfiles(newInternalProfiles);
  };

  const handleToggleDisableLoan = flag => {
    const value = flag !== undefined ? flag : !disableLoan;
    setDisableLoan(value);
    if (value) {
      toast.info(IconToast('info', intl.formatMessage({ id: 'pfa-without-loan-toast' })), { className: 'new-toast' });
    }
    handleChange({ target: { name: 'disableLoan', value: value } });
  };

  const handleProfileChange = event => {
    const index = event.target.value;
    if (!index && index !== 0) {
      return;
    }

    if (disableLoan) {
      handleToggleDisableLoan(false);
    }

    setProfileIndex(index);

    // if (this.props.pFinProfiles) {
    // 	setObj = { ...this.props.pFinProfiles[index], ...setObj };
    // }
  };

  const saveProfiles = async () => {
    if (isGuest) {
      return;
    }
    setUpdated(true);
    const previousProfiles = pFinProfiles;
    const result = await savePFinCalculatorProfiles({ profiles: internalProfiles });
    const savedProfiles = result?.pFinCalculatorProfiles?.profiles;
    if (!Array.isArray(savedProfiles)) {
      return;
    }
    setInternalProfiles(prev =>
      produce(prev, draft => {
        savedProfiles.forEach((saved, index) => {
          if (!draft[index] || !saved?._id) return;
          draft[index]._id = saved._id;
        });
      })
    );
    syncPremiumFinancingIllustrationLinks({
      previousProfiles,
      nextProfiles: savedProfiles
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isGuest) {
        trackDirect('viewPremiumFinancing');
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (_.isEmpty(userDetails)) {
      selectedLocale('zh');
    } else {
      selectedLocale(_.get(userDetails, 'language', ''));
    }
  }, [userDetails, selectedLocale]);

  useEffect(() => {
    renderHeaderButton(!!safariGenerate);
  }, [safariGenerate, renderHeaderButton]);

  useEffect(() => {
    if (!isGuest) {
      getPFinCalculatorProfiles();
    }
  }, [getPFinCalculatorProfiles, isGuest]);

  useEffect(() => {
    if (!isGuest && !safariGenerate && !trial) {
      getIllustrations();
    }
  }, [getIllustrations, isGuest, safariGenerate, trial]);

  useEffect(() => {
    if (prevGetProfilesLoadState !== getProfilesLoadState) {
      if (getProfilesLoadState === SUCCESS) {
        const newProfiles = _.cloneDeep(pFinProfiles);
        setInternalProfiles(newProfiles);
        setLoaded(true);
        setOverrideLoanInterestSliderValue(
          getOverrideLoanInterestSliderValue(newProfiles, profileIndex, defaultProfile)
        );
        setDynamicLoanInterestExpanded(getDynamicLoanInterestExpanded(newProfiles, profileIndex, defaultProfile));
      }
    }
  }, [getProfilesLoadState, prevGetProfilesLoadState, pFinProfiles, profileIndex, defaultProfile]);

  useEffect(() => {
    if (updateProfilesLoadState === ERROR && prevUpdateProfilesLoadState === LOADING) {
      toast.info(IconToast('error', intl.formatMessage({ id: 'todo-err-msg' })), { className: 'new-toast' });
    }
  }, [updateProfilesLoadState, prevUpdateProfilesLoadState, intl]);

  useEffect(() => {
    if (updateProfilesLoadState === SUCCESS && prevUpdateProfilesLoadState === LOADING) {
      toast.info(IconToast('info', intl.formatMessage({ id: 'file-saved-title' })), {
        className: 'new-toast'
      });
      setUpdated(false);
    }
  }, [updateProfilesLoadState, prevUpdateProfilesLoadState, intl]);

  useEffect(() => {
    trackViewership('p-fin-calculater', {
      'Element ID': 'p-fin-calculater',
      'Page Path': window.location.pathname,
      Interval: 180000
    });
  }, []);

  const generate = () => {
    setHintDialog(true);
  };

  const onConfirmGenerate = async ({ password, isPrivateMode }) => {
    if (!loading) {
      try {
        updatePFinCalculatorInputs('loading', true);
        tracking('Click on PDF export - Premium Financing', { adviser: userDetails });

        const options = {
          ...currentProfile,
          pFinProfiles,
          language,
          profileIndex,
          confirmSaveDialog,
          updated,
          loaded,
          graphMode,
          rowOpen,
          fixedLoanInterestExpanded,
          overrideLoanInterestSliderValue,
          dynamicLoanInterestExpanded,
          disableLoan,
          adviser: userDetails,
          outputFormat: 'pdf',
          password,
          isPrivateMode
        };

        // console.log(JSON.stringify(options));

        const pdfBlob = await getPDF('premium-financing', options);
        trackDirect('exportPremiumFinancing');
        await NativeOrWeb.downloadFile(
          pdfBlob,
          `Premium_Financing_${moment().format('DDMMMYYYY')}.pdf`,
          'application/pdf'
        );
      } catch (error) {
        toast.error(error.message);
      } finally {
        updatePFinCalculatorInputs('loading', false);
      }
    }
  };

  const openConfirmDialog = () => setConfirmSaveDialog(true);
  const closeConfirmDialog = () => setConfirmSaveDialog(false);

  const onChangeGraphMode = mode => setGraphMode(mode);
  const closeHintDialog = () => setHintDialog(false);

  const onToggleOpen = index => {
    const newRowOpen = _.set([...rowOpen], `[${index}]`, !_.get(rowOpen, `[${index}]`));
    setRowOpen(newRowOpen);
  };
  const onToggleFixedLoanInterestExpanded = () => {
    setFixedLoanInterestExpanded(!fixedLoanInterestExpanded);
  };
  const onToggleDynamicLoanInterestExpanded = index => {
    const newDynamicLoanInterestExpanded = _.set(
      [...dynamicLoanInterestExpanded],
      `[${index}]`,
      !_.get(dynamicLoanInterestExpanded, `[${index}]`)
    );
    setDynamicLoanInterestExpanded(newDynamicLoanInterestExpanded);
  };

  const openLoanCalculatorDialog = () => setLoanCalculatorDialog(true);
  const closeLoanCalculatorDialog = () => setLoanCalculatorDialog(false);
  const onSubmitLoanCalculator = loan => {
    if (principal) {
      const calculatedLoanPercentage = Math.min(((loan || 0) / principal) * 100, 100);
      handleChange({ target: { name: 'loanPercentage', value: calculatedLoanPercentage } });
    }
  };

  const openBonusCalculatorDialog = () => setBonusCalculatorDialog(true);
  const closeBonusCalculatorDialog = () => setBonusCalculatorDialog(false);
  const onSubmitBonusCalculator = bonus => {
    handleChange({ target: { name: 'firstYearBonus', value: bonus } });
  };

  const openLoanInterestCalculatorDialog = () => setLoanInterestCalculatorDialog(true);
  const closeLoanInterestCalculatorDialog = () => setLoanInterestCalculatorDialog(false);
  const onSubmitLoanInterestCalculator = rate => {
    handleChange({
      target: {
        name: overrideLoanInterest ? 'overrideLoanInterestSliderValue' : 'bankLoanInterest',
        value: rate
      }
    });
  };
  const openEditDisplayNamesDialog = () => setIsEditDisplayNamesDialogOpen(true);
  const closeEditDisplayNamesDialog = () => setIsEditDisplayNamesDialogOpen(false);
  const onConfirmDisplayNames = displayNames => {
    handleChangeProfileNames(displayNames);
    setIsEditDisplayNamesDialogOpen(false);
  };

  var defaultItem = { ...FINANCIAL_CHARTS_HIBOR_ITEM };
  let contents = (
    <div className="pfa-container" id="p-fin-calculater">
      <FinancialChartsButton defaultItem={defaultItem} />

      <ExportHintDialog
        open={hintDialog}
        onClose={closeHintDialog}
        onConfirm={onConfirmGenerate}
        shouldUsePassword={true}
      />

      <LoanCalculatorDialog
        open={loanCalculatorDialog}
        principal={principal}
        day0SurrenderValue={day0SurrenderValue}
        bankLoanPercentage={bankLoanPercentage}
        onChange={handleChange}
        onClose={closeLoanCalculatorDialog}
        onSubmit={onSubmitLoanCalculator}
      />

      <LoanInterestCalculatorDialog
        open={loanInterestCalculatorDialog}
        hiborBase={hiborBase}
        hiborAddOn={hiborAddOn}
        primeBase={primeBase}
        primeSubtract={primeSubtract}
        onChange={handleChange}
        onClose={closeLoanInterestCalculatorDialog}
        onSubmit={onSubmitLoanInterestCalculator}
      />

      <BonusCalculatorDialog
        open={bonusCalculatorDialog}
        principal={principal}
        loanPercentage={disableLoan ? 0 : loanPercentage}
        shouldPayPrincipal={shouldPayPrincipal}
        base={base}
        onChange={handleChange}
        onClose={closeBonusCalculatorDialog}
        onSubmit={onSubmitBonusCalculator}
      />

      <EditDisplayNamesDialog
        open={isEditDisplayNamesDialogOpen}
        onClose={closeEditDisplayNamesDialog}
        displayNames={internalProfiles.map((profile, index) => getPremiumFinancingProfileDisplay(intl, profile, index))}
        onConfirm={onConfirmDisplayNames}
      />

      <PreviewWrapper locale={language}>
        <PremiumFinancing
          {...currentProfile}
          profileIndex={profileIndex}
          graphMode={graphMode}
          rowOpen={rowOpen}
          fixedLoanInterestExpanded={fixedLoanInterestExpanded}
          overrideLoanInterestSliderValue={overrideLoanInterestSliderValue}
          dynamicLoanInterestExpanded={dynamicLoanInterestExpanded}
          adviser={userDetails}
          pFinProfiles={internalProfiles}
          disableLoan={disableLoan}
          handleChange={handleChange}
          handleCheckedChange={handleCheckedChange}
          handleProfileChange={handleProfileChange}
          saveProfiles={openConfirmDialog}
          onChangeGraphMode={onChangeGraphMode}
          onToggleOpen={onToggleOpen}
          onToggleFixedLoanInterestExpanded={onToggleFixedLoanInterestExpanded}
          onToggleDynamicLoanInterestExpanded={onToggleDynamicLoanInterestExpanded}
          onToggleDisableLoan={handleToggleDisableLoan}
          openLoanCalculatorDialog={openLoanCalculatorDialog}
          openBonusCalculatorDialog={openBonusCalculatorDialog}
          openLoanInterestCalculatorDialog={openLoanInterestCalculatorDialog}
          openEditDisplayNamesDialog={openEditDisplayNamesDialog}
          extraSideInputs={
            <PDFExportCard
              loading={loading}
              language={language}
              onChangeLanguage={handleChangeLanguage}
              onGenerate={generate}
            />
          }
          trial={trial}
          isGuest={isGuest}
        />
      </PreviewWrapper>
      {/* More space for PDF export */}
      {/* <Grid container>
        <Grid size={{ xs: 12, md: 9 }}> */}
      {/*{!safariGenerate && !isGuest && (*/}
      {/*  <Accordion*/}
      {/*    defaultExpanded={false}*/}
      {/*    className="analysis-expansion export-expansion export-wrapper exclude-in-image"*/}
      {/*  >*/}
      {/*    <AccordionSummary expandIcon={<ExpandMoreIcon />} className="analysis-expansion-summary1">*/}
      {/*      {intl.formatMessage({ id: 'analysis-export-heading' })}*/}
      {/*    </AccordionSummary>*/}

      {/*    <AccordionDetails className="analysis-expansion-detail">*/}
      {/*      <AnalysisExport*/}
      {/*        intl={intl}*/}
      {/*        history={history}*/}
      {/*        safariGenPath="/premium_financing/safari-gen"*/}
      {/*        targetSelector=".pfa-container"*/}
      {/*        options={targetElem => {*/}
      {/*          // console.log(targetElem, targetElem.offsetWidth)*/}
      {/*          return {*/}
      {/*            // width: targetElem.offsetWidth,*/}
      {/*            onclone: doc => {*/}
      {/*              let container = doc.querySelector('.pfa-container');*/}
      {/*              if (container) {*/}
      {/*                container.style.padding = '10px 8px';*/}
      {/*                container.style.width = `${Math.round(targetElem.offsetWidth)}px`;*/}
      {/*                container.style.fontSize = '85%';*/}

      {/*                // container.querySelectorAll(`input`).forEach(node => node.style.color = 'black');*/}

      {/*                const inputs = container.querySelectorAll('.pfa-input-panel input:not([type="hidden"])');*/}
      {/*                inputs.forEach(node => {*/}
      {/*                  node.style.color = '#665EFF';*/}
      {/*                });*/}

      {/*                const svgs = container.querySelectorAll('svg');*/}
      {/*                svgs.forEach(svg => svg.remove());*/}
      {/*              }*/}
      {/*            }*/}
      {/*          };*/}
      {/*        }}*/}
      {/*        locationState={this.state}*/}
      {/*      />*/}
      {/*    </AccordionDetails>*/}
      {/*  </Accordion>*/}
      {/*)}*/}

      {/* <div className="disclaimer">{intl.formatMessage({ id: 'pfa-disclaimer' })}</div> */}
      {/* </Grid>
      </Grid> */}
      <ConfirmSaveDialog
        open={confirmSaveDialog}
        onClose={closeConfirmDialog}
        onConfirm={saveProfiles}
        isPFinCalculator
      />
      <LeavePageConfirm shouldShow={updated} save={saveProfiles} />
    </div>
  );

  const wrappedContent = (
    <Fragment>
      {trial && isGuest ? (
        <AccessCodeGate
          intl={intl}
          allowed={!isGuest}
          accessCodes={['pfincalcon', 'pfincalone', 'pfincalapo', 'pfincalteam', 'pfincaldynasty']}
        >
          <Container style={{ maxWidth: APP_CONTENT_CONTAINER_MAX_WIDTH, padding: 0 }}>{contents}</Container>

          {/* <div className="top-hint">
            <a rel="noopener noreferrer" href="https://www.portfoplus.com/adviser/" target="_blank">
              <div>PortfoPlus - 專業顧問必備 你的簽單神器</div>
              <div>{'了解更多>'}</div>
            </a>
          </div> */}
        </AccessCodeGate>
      ) : (
        <>
          <LoadingBackdrop open={getProfilesLoadState === LOADING || updateProfilesLoadState === LOADING} />
          {(getProfilesLoadState === SUCCESS || getProfilesLoadState === ERROR) && contents}
        </>
      )}
    </Fragment>
  );

  return (
    <div className={`pfa ${safariGenerate ? 'safari-gen' : ''} ${isGuest ? 'guest' : ''}`}>
      {safariGenerate || isGuest ? (
        <div style={{ padding: '50px 0' }}>{wrappedContent}</div>
      ) : (
        <AppContentContainer>{wrappedContent}</AppContentContainer>
      )}
    </div>
  );
};

const container = connect(
  // Map state to props
  state => ({
    userDetails: state.user.userDetails,
    pFinProfiles: state.pFinCalculator.profiles,
    getProfilesLoadState: state.pFinCalculator.getProfilesLoadState,
    updateProfilesLoadState: state.pFinCalculator.updateProfilesLoadState,
    loading: state.pFinCalculator.loading
  }),
  // Map actions to props
  {
    selectedLocale,
    renderHeaderButton,
    getPFinCalculatorProfiles,
    savePFinCalculatorProfiles,
    updatePFinCalculatorProfiles,
    updatePFinCalculatorInputs,
    toggleDialog,
    getIllustrations
  },
  // mergeProps
  null,
  // options
  {}
)(PremiumFinancingAnalysis);

export default injectIntl(container);
